// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ScrollIndicator } from '@/components/scroll-indicator';
import { Search as SearchIcon } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import { useCustomers } from '@/customer/hooks/use-customers';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash-es';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  onCustomerSelect: (customer: Customer) => void;
  excludeCustomerId?: string;
}

const SEARCH_DEBOUNCE_MS = 300;
// Fixed height so the list contents never resize the merge dialog
const LIST_HEIGHT_PX = 450;

export function CustomerSelectionList(props: Props) {
  // Archived customers are included so they can be merged away
  const { customers } = useCustomers({ filter: { isArchived: 'all' } });
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const scrollIndicator = useScrollIndicator();
  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  const debouncedSetSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setDebouncedSearch(searchValue);
      }, SEARCH_DEBOUNCE_MS),
    [setDebouncedSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);
      debouncedSetSearch(value);
    },
    [setSearchValue, debouncedSetSearch]
  );

  const filteredCustomers = useMemo(() => {
    let filtered = customers.filter(
      (customer) => customer.id !== props.excludeCustomerId
    );

    if (debouncedSearch.trim() !== '') {
      filtered = filtered.filter((customer) =>
        customer.name.toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    }

    return filtered;
  }, [customers, props.excludeCustomerId, debouncedSearch]);

  return (
    <Stack spacing={2}>
      <TextField
        placeholder="Search companies"
        value={searchValue}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Paper
        variant="outlined"
        sx={{ height: LIST_HEIGHT_PX, overflow: 'auto', position: 'relative' }}
        ref={scrollIndicator.ref}
      >
        <ScrollIndicator
          label="More"
          isVisible={scrollIndicator.isVisible}
          onClick={handleClickScroll}
        />
        <List dense>
          {filteredCustomers.map((customer, index) => (
            <Box key={customer.id}>
              <CustomerListItem
                customer={customer}
                onSelect={() => props.onCustomerSelect(customer)}
              />
              {index < filteredCustomers.length - 1 && (
                <Divider sx={{ width: '90%', mx: 'auto' }} />
              )}
            </Box>
          ))}
          {filteredCustomers.length === 0 && (
            <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
              <Typography variant="body2" color="text.secondary">
                {debouncedSearch.trim() === ''
                  ? 'No companies found'
                  : `No companies found for ${debouncedSearch}`}
              </Typography>
            </Box>
          )}
        </List>
      </Paper>
    </Stack>
  );
}

interface CustomerListItemProps {
  customer: Customer;
  onSelect: () => void;
}

function CustomerListItem(props: CustomerListItemProps) {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={props.onSelect}>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1">{props.customer.name}</Typography>
              <Chip
                size="small"
                label="Customer"
                color="secondary"
                variant={props.customer.isCustomer ? 'filled' : 'outlined'}
              />
              <Chip
                size="small"
                label="Supplier"
                color="secondary"
                variant={props.customer.isSupplier ? 'filled' : 'outlined'}
              />
              {props.customer.isArchived && (
                <Chip
                  size="small"
                  label="Archived"
                  variant="outlined"
                  color="default"
                />
              )}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              {pluralize(props.customer.activeProjectCt, {
                plural: 'projects',
                singular: 'project',
              })}
              ,{' '}
              {pluralize(props.customer.locationCount, {
                plural: 'locations',
                singular: 'location',
              })}
              ,{' '}
              {pluralize(props.customer.assetCount, {
                plural: 'assets owned',
                singular: 'asset owned',
              })}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  );
}
